/**
 * AWS Amplify Configuration
 *
 * Configures Amplify with runtime config loaded from /config.json
 * Supports both Hosted UI (OAuth) and SRP (username/password) flows
 */

import { Amplify } from 'aws-amplify';
import type { ResourcesConfig } from 'aws-amplify';
import { getConfig } from '$config';

let isConfigured = false;

/**
 * Configure Amplify with runtime config
 * Call this before any auth operations
 */
export function configureAmplify(): void {
  if (isConfigured) {
    return;
  }

  const config = getConfig();
  const oauth = config.auth.oauth;

  const amplifyConfig: ResourcesConfig = {
    Auth: {
      Cognito: {
        userPoolId: config.auth.user_pool_id,
        userPoolClientId: config.auth.user_pool_client_id,
        identityPoolId: config.auth.identity_pool_id,
        loginWith: {
          username: true,
          email: true,
          // Hosted-UI social sign-in (Google/Apple/Microsoft). Amplify picks
          // the redirect URI matching the current origin from the arrays.
          ...(oauth
            ? {
                oauth: {
                  domain: oauth.domain,
                  scopes: oauth.scopes,
                  redirectSignIn: oauth.redirect_sign_in_uri,
                  redirectSignOut: oauth.redirect_sign_out_uri,
                  responseType: oauth.response_type,
                },
              }
            : {}),
        },
        signUpVerificationMethod: 'code',
        userAttributes: {
          email: {
            required: true,
          },
        },
        allowGuestAccess: false,
        passwordFormat: {
          minLength: 8,
          requireLowercase: true,
          requireUppercase: true,
          requireNumbers: true,
          requireSpecialCharacters: true,
        },
      },
    },
  };

  Amplify.configure(amplifyConfig);
  isConfigured = true;
}

/**
 * Check if Amplify is configured
 */
export function isAmplifyConfigured(): boolean {
  return isConfigured;
}

/**
 * Reset configuration (useful for testing)
 */
export function resetAmplifyConfig(): void {
  isConfigured = false;
}
